import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Container from "../components/layout/Container";
import OfferCards from "../components/offers/OfferCards";
import SubNav, { defensieSubNavItems } from "../components/SubNav";
import OfficialStandardsSection from "../components/preparation/OfficialStandardsSection";
import { getOfficialStandardText } from "../data/defensieStandards";

const pageUrl = "https://missionmovement.nl/voorbereiding-defensie";
const ogImage = "https://missionmovement.nl/img/defensiekeuring.png";

const physicalPreparationItems = [
  {
    title: "Belastbaarheid",
    text: "Pezen, gewrichten, voeten, rug en schouders hebben tijd nodig om te wennen aan herhaalde impact, dragen, trekken en herstel onder vermoeidheid.",
  },
  {
    title: "Conditionele tolerantie",
    text: "Voorbereiding Defensie gaat verder dan een 12-minutenloop of een goede 5K. Je moet kunnen blijven bewegen, herstellen tussen inspanningen en opnieuw leveren.",
  },
  {
    title: "Herhaalbare output",
    text: "Hardlopen, marsen, dragen, calisthenics, zwemmen en slaaptekort stapelen op. Het doel is niet een perfecte testdag, maar bruikbare capaciteit die terugkomt.",
  },
];

const behaviorItems = [
  {
    title: "Groepsdynamiek",
    text: "Je traint individueel, maar de opleiding is sociaal. De groep merkt hoe je communiceert, helpt en reageert wanneer je moe, gefrustreerd of onzeker bent.",
  },
  {
    title: "Onmogelijke groepsopdrachten",
    text: "Sommige opdrachten draaien niet vooral om winnen. Ze laten zien of je blijft samenwerken, initiatief neemt en constructief blijft wanneer er geen nette oplossing is.",
  },
  {
    title: "Leiderschap & teamgedrag",
    text: "Soms leid je, soms volg je. Wat telt is initiatief zonder ego, duidelijke communicatie, eigenaarschap en betrouwbaar blijven onder druk.",
  },
];

const unitLinks = [
  {
    title: "Korps Mariniers",
    path: "/units/mariniers",
    text: "Voor kandidaten die richting amfibische infanterie en zware mariniersopleiding willen werken.",
  },
  {
    title: "Korps Commandotroepen",
    path: "/units/commandotroepen",
    text: "Voor wie zich wil verdiepen in de route richting het KCT en specialistische voorbereiding.",
  },
  {
    title: "11 Luchtmobiele Brigade",
    path: "/units/luchtmobiel",
    text: "Voor kandidaten richting de rode baretten, snelheid, inzetbaarheid en fysieke basis.",
  },
  // {
  //   title: "Veiligheidsdiensten",
  //   path: "/units/veiligheidsdiensten",
  //   text: "Voor politie, brandweer, KMar en andere functies waar voorbereiding onder druk telt.",
  // },
];

const faqItems = [
  {
    question: "Wat toetst de functionele fysieke aanstellingskeuring voor Cluster 6?",
    answer: getOfficialStandardText("cluster6"),
  },
  {
    question: "Hoe bereid ik me fysiek voor op Defensie?",
    answer:
      "Begin met een brede fysieke basis: kracht, conditie, mobiliteit, techniek en herstel. Train niet alleen voor één testmoment, maar bouw aan belastbaarheid zodat je lichaam ook langere periodes van training en vermoeidheid aankan.",
  },
  {
    question: "Zijn de minimale selectie-eisen genoeg?",
    answer:
      "De minimale eisen zijn een startpunt, geen einddoel. Wie alleen traint om de norm te halen, mist vaak de marge die nodig is tijdens opleiding, herhaling en langdurige belasting.",
  },
  {
    question: "Kan Mission Movement helpen als ik nog niet op niveau ben?",
    answer:
      "Ja. Je hoeft nog niet klaar te zijn om te starten. Het belangrijkste is dat je eerlijk kijkt naar waar je nu staat en bereid bent om gestructureerd te werken aan wat nodig is.",
  },
  {
    question: "Moet ik al op niveau zijn om te starten?",
    answer:
      "Je hoeft geen topatleet te zijn, maar je moet wel consistent kunnen trainen en bereid zijn belasting eerlijk te schalen. Een goed startpunt is dat je pijnvrij kunt wandelen, rustig kunt hardlopen en basisoefeningen technisch kunt aanleren. Bij terugkerende pijn, eerdere zware blessures of twijfel over je belastbaarheid is het verstandig eerst advies te vragen aan een arts, fysiotherapeut of coach.",
  },
  {
    question: "Is deze voorbereiding alleen voor Defensie?",
    answer:
      "Nee. Mission Movement richt zich ook op kandidaten richting politie, brandweer, KMar en andere veiligheidsdiensten waar fysieke en mentale voorbereiding belangrijk is.",
  },
];

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Voorbereiding Defensie",
  url: pageUrl,
  inLanguage: "nl-NL",
  description:
    "Bereid je gericht voor op Defensie, selectie en opleiding met aandacht voor belastbaarheid, conditionele tolerantie, groepsdynamiek en duurzame voorbereiding.",
  about: {
    "@type": "Thing",
    name: "Militaire voorbereiding",
  },
  publisher: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
  },
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Voorbereiding Defensie en Veiligheidsdiensten",
  url: pageUrl,
  provider: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
  },
  areaServed: {
    "@type": "Country",
    name: "Netherlands",
  },
  serviceType:
    "Militaire voorbereiding, fysieke training en mentale voorbereiding",
  audience: {
    "@type": "Audience",
    audienceType:
      "Kandidaten richting Defensie, politie, brandweer, KMar en specialistische eenheden",
  },
  description:
    "Mission Movement helpt kandidaten richting Defensie gestructureerd bouwen aan kracht, conditie, belastbaarheid, discipline en voorbereiding onder druk.",
};

const faqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

const VoorbereidingDefensie = () => {
  return (
    <main className="min-h-screen bg-mmPage text-mmText">
      <Helmet>
        <title>Voorbereiding Defensie | Belastbaarheid, Selectie & Opleiding</title>
        <meta
          name="description"
          content="Bereid je gericht voor op Defensie, selectie en opleiding met aandacht voor belastbaarheid, conditionele tolerantie, groepsdynamiek en duurzame voorbereiding."
        />
        <meta
          property="og:title"
          content="Voorbereiding Defensie | Mission Movement"
        />
        <meta
          property="og:description"
          content="Mission Movement helpt kandidaten richting Defensie bouwen aan belastbaarheid, conditionele tolerantie, groepsdynamiek en gerichte voorbereiding."
        />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={pageUrl} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqPageSchema)}
        </script>
      </Helmet>

      <section className="relative min-h-[400px] md:h-[500px] flex items-center overflow-hidden border-b border-mmBorder">
        <img
          src="/img/defensiekeuring.png"
          alt="Kandidaten onderweg naar de Defensie keuring"
          className="absolute inset-0 h-full w-full object-cover object-center"
          width={858}
          height={536}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        <div className="absolute inset-0 mm-heroOverlay" />
        <Container className="relative z-10 py-12 md:py-14">
          <div className="max-w-4xl">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
              Voorbereiding Defensie
            </p>
            <h1 className="font-display uppercase tracking-[0.08em] sm:tracking-widest text-white text-[32px] md:text-[54px] leading-[0.98] break-words max-w-full">
              Voorbereiding op Defensie
            </h1>
            <div className="h-[3px] w-12 bg-mmAccent rounded-full mt-4 mb-5" />
            <div className="w-full max-w-2xl bg-black/65 border border-white/10 rounded-2xl px-6 py-5 shadow-sm">
              <p className="text-white/95 text-[15px] md:text-lg leading-relaxed">
                Niet alleen trainen om de norm te halen, maar bouwen aan een
                lichaam en mindset die selectie, opleiding en het werk daarna
                aankunnen.
              </p>
            </div>
            <div className="mt-8">
              <Link to="/program">
                <button className="mm-btnPrimary px-8">
                  Bekijk het 12-weekse programma
                </button>
              </Link>
            </div>
          </div>
        </Container>
      </section>

      <div className="relative shadow-sm">
        <SubNav variant="dark" scrollable items={defensieSubNavItems} />
      </div>

      <OfficialStandardsSection />

      <section className="py-8 border-b border-mmBorder">
        <Container>
          <div className="border-l-4 border-mmAccent bg-mmSurface px-5 py-5 md:px-7 md:py-6 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="max-w-3xl">
                <h2 className="font-display uppercase tracking-widest text-mmText text-xl md:text-2xl">
                  Bereid je je voor op Defensie?
                </h2>
                <p className="mt-2 text-sm md:text-base text-mmTextMuted leading-relaxed">
                  Doe eerst de gratis check en ontdek of je huidige training
                  aansluit op wat jouw richting straks van je vraagt.
                </p>
              </div>
              <Link
                to="/voorbereiding-check"
                className="mm-btnPrimary inline-flex justify-center shrink-0"
              >
                Doe de check
              </Link>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">Waarom voorbereiding meer is dan fit zijn</h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Minimumeisen halen is niet hetzelfde als klaar zijn voor
                selectie, opleiding en het werk daarna. Een test meet of je een
                grens haalt op een bepaald moment. Voorbereiding Defensie moet
                verder kijken: wat kun je blijven leveren wanneer belasting,
                vermoeidheid en druk zich opstapelen?
              </p>
              <p>
                Fit zijn in de sportschool is iets anders dan functioneren
                binnen hiërarchie, herhaling en groepsconsequenties. Je hebt
                fysieke capaciteit nodig, maar ook duurzaamheid, herstel en
                gedrag dat bruikbaar blijft wanneer comfort wegvalt.
              </p>
              <p>
                Goede voorbereiding bouwt daarom aan kracht, conditie,
                belastbaarheid, techniek en gedrag onder stress. Niet om succes
                te beloven, maar om met meer marge aan een serieus traject te
                beginnen.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">
              Minimumeisen zijn geen eindpunt
            </h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Veel mensen richten zich vooral op de zichtbare eisen:
                hardlooptijden, krachtstandaarden of testmomenten. Dat is
                begrijpelijk, maar vaak te beperkt. Een norm halen betekent niet
                automatisch dat je lichaam klaar is voor langdurige belasting.
              </p>
              <p>
                De functionele aanstellingskeuring en eventuele aanvullende
                eenheidstests beoordelen verschillende eisen.
                De opleiding laat zien of je lichaam die belasting dag na dag en
                week na week kan blijven dragen. Daarom is voorbereiding meer
                dan trainen voor een goed meetmoment.
              </p>
              <p>
                Binnen Defensie krijg je te maken met vermoeidheid, beperkte
                hersteltijd, herhaling, onverwachte opdrachten en weinig regie
                over de omstandigheden. Juist daar wordt zichtbaar of je alleen
                fit bent, of werkelijk voorbereid.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl mb-10">
            <h2 className="mm-h2 mb-6">
              Belastbaarheid: het lichaam dat de opleiding moet dragen
            </h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
              Selectie en opleiding bestaan niet uit één perfecte dag. Ze vragen
              herhaalde belasting. Lopen, dragen, marsen, calisthenics, zwemmen
              en slaaptekort stapelen zich op. Daarom moet kracht en
              uithoudingsvermogen elkaar ondersteunen in plaats van los naast
              elkaar te bestaan.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {physicalPreparationItems.map((item, index) => (
              <article key={item.title} className="mm-card p-6">
                <div className="h-9 w-9 rounded-full border border-mmBorderStrong flex items-center justify-center text-mmAccent font-semibold mb-5">
                  {index + 1}
                </div>
                <h3 className="font-display uppercase tracking-widest text-mmAccent text-base mb-3">
                  {item.title}
                </h3>
                <p className="text-mmTextMuted text-sm leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">
              Gedrag onder druk wordt zichtbaar in de groep
            </h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Veel training doe je alleen. Een opleiding doe je nooit alleen.
                De groep ziet hoe je reageert wanneer je moe bent, fouten maakt,
                weinig informatie krijgt of niet direct grip hebt op de
                situatie.
              </p>
              <p>
                Fysiek sterk zijn is niet genoeg wanneer je de groep onrustig
                maakt, slecht luistert of onder druk alleen nog met jezelf bezig
                bent. Bruikbaar gedrag telt: communiceren, helpen, luisteren,
                verantwoordelijkheid nemen en blijven bijdragen.
              </p>
            </div>
          </div>
          <div className="grid gap-5 md:grid-cols-3 mt-10">
            {behaviorItems.map((item, index) => (
              <article key={item.title} className="mm-card p-6">
                <div className="h-9 w-9 rounded-full border border-mmBorderStrong flex items-center justify-center text-mmAccent font-semibold mb-5">
                  {index + 1}
                </div>
                <h3 className="font-display uppercase tracking-widest text-mmAccent text-base mb-3">
                  {item.title}
                </h3>
                <p className="text-mmTextMuted text-sm leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="grid gap-5 lg:grid-cols-2">
            <article className="mm-card p-6 md:p-8">
              <h2 className="font-display uppercase tracking-widest text-mmAccent text-xl md:text-2xl mb-4">
                Lang van huis zijn
              </h2>
              <div className="space-y-4 text-mmTextMuted text-base md:text-lg leading-relaxed">
                <p>
                  Voorbereiding is niet alleen fysiek. Tijdens selectie en
                  opleiding krijg je te maken met afstand van huis, minder
                  comfort, minder autonomie en een routine die niet om jouw
                  voorkeuren draait.
                </p>
                <p>
                  Discipline, ritme, verwachtingen en omgaan met ongemak horen
                  daarom ook bij voorbereiding. Niet als stoer verhaal, maar als
                  praktisch onderdeel van inzetbaar blijven.
                </p>
              </div>
            </article>

            <article className="mm-card p-6 md:p-8">
              <h2 className="font-display uppercase tracking-widest text-mmAccent text-xl md:text-2xl mb-4">
                De poort in en de poort uit
              </h2>
              <div className="space-y-4 text-mmTextMuted text-base md:text-lg leading-relaxed">
                <p>
                  Wie je buiten de poort bent, is niet automatisch wie je binnen
                  selectie of opleiding laat zien. Buiten heb je meer comfort,
                  keuzevrijheid en controle. Binnen gelden standaarden,
                  hiërarchie, herhaling en consequenties voor de groep.
                </p>
                <p>
                  Serieuze voorbereiding verkleint het gat tussen comfortleven
                  en opleidingsleven. Niet door toneel te spelen, maar door
                  gewoonten te bouwen die ook overeind blijven wanneer jij niet
                  de omstandigheden bepaalt.
                </p>
              </div>
            </article>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">
              Waarom doorbijten geen trainingsmethode is
            </h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Doorbijten is een vereiste wanneer het zwaar wordt. Het is geen
                trainingsmethode. Willekeurig lijden, steeds harder gaan of
                pijn negeren is geen bewijs van voorbereiding.
              </p>
              <p>
                Slimme voorbereiding gebruikt progressie, belastingsturing,
                herstel, techniek, structuur en eerlijke feedback. Zo ontwikkel
                je capaciteit die inzetbaar blijft, in plaats van alleen te
                bewijzen dat je vandaag diep kunt gaan.
              </p>
              <p>
                Discipline betekent dus ook op tijd bijsturen, zwakke schakels
                aanpakken en begrijpen waarom een training op dat moment nodig
                is. Hard werken blijft nodig. Doordacht werken maakt dat harde
                werk bruikbaarder.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">
              Voor welke richting werk je?
            </h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Voorbereiding Defensie heeft een gedeelde basis, maar het Korps
                Mariniers, het Korps Commandotroepen en 11 Luchtmobiele Brigade
                leggen elk andere accenten. Richting geeft betekenis aan je
                training.
              </p>
              <p>
                Je hoeft nog niet op niveau te zijn. Je moet wel bereid zijn om
                eerlijk te kijken naar waar je staat en gericht te bouwen aan
                wat nodig is.
              </p>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2 mt-10">
            {unitLinks.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="group mm-card p-6 block transition-transform hover:-translate-y-[2px] hover:border-mmAccent/40"
              >
                <h3 className="font-display uppercase tracking-widest text-mmText group-hover:text-mmAccent transition-colors text-lg mb-3">
                  {item.title}
                </h3>
                <p className="text-mmTextMuted text-sm md:text-base leading-relaxed">
                  {item.text}
                </p>
                <span className="inline-block mt-5 text-mmAccent font-semibold uppercase tracking-widest text-xs">
                  Bekijk route
                </span>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">De rol van Mission Movement</h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Mission Movement geeft richting aan je voorbereiding. Je leert
                niet alleen wat je moet trainen, maar ook waarom. De aanpak is
                gebouwd op militaire praktijkervaring, coaching sinds 2014 en
                jarenlange ervaring met fysieke ontwikkeling, techniek,
                belastbaarheid en mentale voorbereiding.
              </p>
              <p>
                De aanpak is praktijkgedreven en operationeel geïnspireerd:
                trainen met structuur, progressie, herstel en bijsturing. Elke
                training heeft een functie binnen de grotere opbouw.
              </p>
              <p>
                Het doel is niet alleen om door een selectie heen te komen. Het
                doel is om sterker, slimmer en beter voorbereid te starten aan
                het traject waar je serieus voor kiest.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-8">Veelgestelde vragen</h2>
            <div className="space-y-4">
              {faqItems.map((item) => (
                <article key={item.question} className="mm-card p-6">
                  <h3 className="font-display uppercase tracking-widest text-mmAccent text-base md:text-lg mb-3">
                    {item.question}
                  </h3>
                  <p className="text-mmTextMuted text-base leading-relaxed">
                    {item.answer}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mm-h2 mb-5">Start gericht met je voorbereiding</h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              Kies de Defensie-route die past bij hoe jij wilt starten:
              zelfstandig met structuur, of met persoonlijke begeleiding rond
              jouw doel, startniveau en voortgang.
            </p>
          </div>
        </Container>
        <OfferCards variant="defensie" context="embedded" />
      </section>
    </main>
  );
};

export default VoorbereidingDefensie;
